import { useEffect, useRef, useState, type ReactNode } from 'react';
import { t } from '../lib/i18n';

/**
 * A dialog for something a customer will feel.
 *
 * Suspending a tenant, moving it between plans, opening a support session: each is an action taken
 * by somebody who is not the customer, and each has to be explained afterwards. So the reason is
 * required before the button is live, and it is the reason the API stores — not a placeholder the
 * interface invented.
 *
 * `<dialog>` rather than a div, so Escape, focus trapping and the backdrop come from the browser
 * rather than from three hundred lines of our own.
 */
export function ConfirmAction({
  title,
  description,
  confirmLabel,
  tone = 'default',
  open,
  busy,
  error,
  children,
  onConfirm,
  onCancel,
}: {
  title: string;
  description?: string;
  confirmLabel: string;
  tone?: 'default' | 'danger';
  open: boolean;
  busy?: boolean;
  error?: string | null;
  /** Extra fields the action needs — a plan to move to, a number of days. */
  children?: ReactNode;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}): React.JSX.Element | null {
  const dialog = useRef<HTMLDialogElement>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const element = dialog.current;

    if (!element) return;

    if (open && !element.open) {
      setReason('');
      element.showModal();
    }

    if (!open && element.open) element.close();
  }, [open]);

  if (!open) return null;

  return (
    <dialog ref={dialog} className="confirm" onCancel={onCancel} onClose={onCancel}>
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (reason.trim().length >= 3) onConfirm(reason.trim());
        }}
      >
        <h2>{title}</h2>
        {description && <p className="muted">{description}</p>}

        {children}

        <label className="stack__field">
          <span>{t('confirm.reason')}</span>
          <textarea
            rows={2}
            value={reason}
            placeholder={t('confirm.reasonPlaceholder')}
            onChange={(event) => setReason(event.target.value)}
            autoFocus
          />
        </label>

        <p className="muted small">{t('confirm.reasonHint')}</p>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onCancel}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className={`button button--compact ${
              tone === 'danger' ? 'button--danger' : 'button--primary'
            }`}
            disabled={reason.trim().length < 3 || busy}
          >
            {busy ? t('confirm.working') : confirmLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
